/**
 * MissedClockInApprovals — Wave 3.2 live-API wrapper around <ApprovalList>.
 *
 * Backend canonical payload for type='missed_clock_in':
 *   { date, time, reason }
 * Historical rows may still carry expected_time/requested_time; rendering
 * remains backward-compatible, while every new write uses `time`.
 */

import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import ApprovalList, { ApprovalColumn, ApprovalFormField } from './ApprovalList';
import { useTranslation } from 'react-i18next';

function pickStr(data: unknown, key: string): string {
  return typeof data === 'object' && data !== null && key in data
    ? String((data as Record<string, unknown>)[key] ?? '')
    : '';
}

const MissedClockInApprovals: React.FC = () => {
  const { t } = useTranslation();

  const columns = useMemo<ApprovalColumn[]>(() => [
    { label: t('attendance:approvals.missedClockIn.colDate'), render: (r) => pickStr(r.data, 'date') || '—' },
    {
      label: t('attendance:approvals.missedClockIn.colRequestedTime'),
      render: (r) =>
        pickStr(r.data, 'time') || pickStr(r.data, 'expected_time') ||
        pickStr(r.data, 'requested_time') || '—',
    },
    {
      label: t('attendance:approvals.missedClockIn.colReason'),
      render: (r) => (
        <Typography variant="caption" color="text.secondary">
          {pickStr(r.data, 'reason') || '—'}
        </Typography>
      ),
    },
  ], [t]);

  const createFields = useMemo<ApprovalFormField[]>(() => [
    { key: 'date', label: t('attendance:approvals.missedClockIn.colDate'), type: 'date', required: true, span: 6 },
    { key: 'time', label: t('attendance:approvals.missedClockIn.formExpectedTime'), type: 'time', required: true, span: 6 },
    // `missed_type` lived here as an admin-only select. NOTHING reads it: the correction
    // path adds a bare `TimePunch` and re-derives the entry, inferring clock-in from the
    // earliest punch and clock-out from the latest, so the direction is never taken from
    // the payload. Its column was therefore blank for every worker- or phone-filed
    // request, while this form asked the admin for it. Deleted with its column, for the
    // same reason as off-site `work_type` (2026-08-25).
    { key: 'reason', label: t('attendance:approvals.missedClockIn.colReason'), type: 'textarea', required: true },
  ], [t]);

  return (
    <ApprovalList
      type="missed_clock_in"
      title={t('attendance:approvals.missedClockInTitle')}
      description={t('attendance:approvals.missedClockInDescription')}
      recordTitle={t('attendance:approvals.missedClockIn.recordTitle')}
      filterLabel={t('attendance:approvals.filterLabel')}
      columns={columns}
      createFields={createFields}
    />
  );
};

export default MissedClockInApprovals;
